import cn from 'classnames'
import { TextAreaInputVariant } from 'ext-ui/components/TextAreaInput/TextAreaInput'
import React, {
  ComponentProps,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react'

import styles from './TextAreaInputInternal.style'

type TextAreaInputInternalProps = ComponentProps<'textarea'> & {
  variant: TextAreaInputVariant
  autoResize?: boolean
}

const TextAreaInputInternal = forwardRef<
  HTMLTextAreaElement,
  TextAreaInputInternalProps
>((props, ref) => {
  const { variant, className, disabled, readOnly, value, autoResize, ...rest } =
    props
  const internalRef = useRef<HTMLTextAreaElement | null>(null)

  const mergedRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      internalRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )

  const adjustHeight = useCallback(() => {
    const textarea = internalRef.current
    if (!textarea || !autoResize) return
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }, [autoResize])

  useLayoutEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  return (
    <textarea
      css={styles}
      className={cn(className, variant, { 'auto-resize': autoResize })}
      disabled={disabled}
      value={value}
      readOnly={readOnly}
      {...rest}
      onInput={(e) => {
        adjustHeight()
        rest.onInput?.(e)
      }}
      ref={mergedRef}
    />
  )
})

export default TextAreaInputInternal
